// Frontend copy of api/chat/coachActionsSchema.js.
//
// WHY DUPLICATED:
//   The schema is conceptually one source of truth, but the React app
//   (Create React App, webpack) refuses imports from outside src/ via
//   the ModuleScopePlugin. Without ejecting CRA or adding a build-tool
//   override, we cannot import api/chat/coachActionsSchema.js from
//   inside src/. So we maintain a frontend copy.
//
// HOW DRIFT IS PREVENTED:
//   src/utils/coachActionsSchema.test.js asserts this file's
//   exported responseFormat is structurally identical to the api/
//   copy. CI will fail if they diverge.
//
// WHEN TO EDIT THIS FILE:
//   Always edit BOTH files together. The change in api/chat/ is what
//   the server actually sends to Azure OpenAI; the change here is what
//   the client requests. If they disagree, the structured path breaks.

const VALID_SPORTS    = ['swim', 'bike', 'run', 'strength', 'brick', 'other'];
const VALID_PHASE     = ['base', 'build', 'peak', 'taper', 'race'];
const VALID_MILESTONE = ['ftp_test', 'race_simulation', 'taper_start', 'race', 'key_workout', 'rest_week'];
const VALID_ZONES     = ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'];

export const ACTION_TYPES = [
  'add_workouts',
  'remove_workouts',
  'add_phase',
  'modify_phase',
  'remove_phase',
  'add_milestone',
  'remove_milestone',
];

const workoutItemSchema = {
  type: 'object',
  properties: {
    date:        { type: 'string', description: 'ISO date YYYY-MM-DD' },
    sport:       { type: 'string', enum: VALID_SPORTS },
    title:       { type: 'string', description: 'Short workout title shown on the card.' },
    duration:    { type: 'integer', description: 'Duration in minutes.' },
    zone:        { type: ['string', 'null'], enum: [...VALID_ZONES, null], description: 'Primary intensity zone (Z1-Z5), or null if mixed/N/A.' },
    description: { type: ['string', 'null'], description: 'Workout details (warmup/main/cooldown). May be null for very simple sessions.' },
  },
  required: ['date', 'sport', 'title', 'duration', 'zone', 'description'],
  additionalProperties: false,
};

const addWorkoutsPayload = {
  type: 'object',
  properties: {
    items: {
      type: 'array',
      description: 'Workouts to add. May span multiple weeks in one proposal.',
      items: workoutItemSchema,
    },
  },
  required: ['items'],
  additionalProperties: false,
};

const removeWorkoutsPayload = {
  type: 'object',
  properties: {
    workoutIds: {
      type: 'array',
      description: 'IDs of existing planned workouts to remove. Use only IDs present in the context dump.',
      items: { type: 'string' },
    },
  },
  required: ['workoutIds'],
  additionalProperties: false,
};

const phaseShape = {
  type: 'object',
  properties: {
    name:         { type: 'string', description: 'Human-readable phase name shown on the timeline.' },
    type:         { type: 'string', enum: VALID_PHASE },
    startDate:    { type: 'string', description: 'ISO date YYYY-MM-DD. App snaps to Monday at apply time.' },
    endDate:      { type: 'string', description: 'ISO date YYYY-MM-DD. App snaps to Sunday at apply time.' },
    notes:        { type: ['string', 'null'], description: 'Free-text rationale; nullable.' },
    weeklyTSS:    { type: ['integer', 'null'], description: 'Target weekly TSS for this phase; nullable.' },
    targetRaceId: { type: ['string', 'null'], description: 'ISO date YYYY-MM-DD of the race this phase builds toward (e.g. "2026-07-05"). MUST match the exact date string from the athlete\'s race list shown in context — do NOT prefix with race name, "race:" or any other label. Set null if the phase is generic / not tied to a specific race.' },
  },
  required: ['name', 'type', 'startDate', 'endDate', 'notes', 'weeklyTSS', 'targetRaceId'],
  additionalProperties: false,
};

const addPhasePayload = phaseShape;

const modifyPhasePayload = {
  type: 'object',
  properties: {
    phaseId: { type: 'string', description: 'EXACT existing phase ID from the context dump. Do not invent.' },
    changes: {
      type: 'object',
      description: 'Per-field overrides. Set a field to null to leave it unchanged. Set at least one field.',
      properties: {
        name:         { type: ['string', 'null'] },
        type:         { type: ['string', 'null'], enum: [...VALID_PHASE, null] },
        startDate:    { type: ['string', 'null'] },
        endDate:      { type: ['string', 'null'] },
        notes:        { type: ['string', 'null'] },
        weeklyTSS:    { type: ['integer', 'null'] },
        targetRaceId: { type: ['string', 'null'] },
      },
      required: ['name', 'type', 'startDate', 'endDate', 'notes', 'weeklyTSS', 'targetRaceId'],
      additionalProperties: false,
    },
  },
  required: ['phaseId', 'changes'],
  additionalProperties: false,
};

const removePhasePayload = {
  type: 'object',
  properties: {
    phaseId: { type: 'string', description: 'EXACT existing phase ID from the context dump.' },
  },
  required: ['phaseId'],
  additionalProperties: false,
};

const milestoneShape = {
  type: 'object',
  properties: {
    date:         { type: 'string', description: 'ISO date YYYY-MM-DD.' },
    label:        { type: 'string', description: 'Short label shown on the timeline (e.g. "FTP Test").' },
    type:         { type: ['string', 'null'], enum: [...VALID_MILESTONE, null], description: 'Milestone category; null if generic.' },
    targetRaceId: { type: ['string', 'null'], description: 'ISO date YYYY-MM-DD of the race this milestone supports (e.g. "2026-07-05"). MUST match the exact date string from the athlete\'s race list — do NOT invent composite IDs. Set null for generic milestones.' },
    notes:        { type: ['string', 'null'], description: 'Free-text rationale; nullable.' },
  },
  required: ['date', 'label', 'type', 'targetRaceId', 'notes'],
  additionalProperties: false,
};

const addMilestonePayload = milestoneShape;

const removeMilestonePayload = {
  type: 'object',
  properties: {
    milestoneId: { type: 'string', description: 'EXACT existing milestone ID from the context dump.' },
  },
  required: ['milestoneId'],
  additionalProperties: false,
};

// ── Proposal schema factory ─────────────────────────────────────────────
// See api/chat/coachActionsSchema.js for the rationale — same factory,
// duplicated here because CRA's ModuleScopePlugin forbids importing from
// outside src/. The drift test asserts the exported `responseFormat` and
// `phaseSkeletonResponseFormat` stay structurally identical across both
// files.

const PAYLOAD_BY_TYPE = {
  add_workouts:     addWorkoutsPayload,
  remove_workouts:  removeWorkoutsPayload,
  add_phase:        addPhasePayload,
  modify_phase:     modifyPhasePayload,
  remove_phase:     removePhasePayload,
  add_milestone:    addMilestonePayload,
  remove_milestone: removeMilestonePayload,
};

function buildProposalSchema(allowedActionTypes, description) {
  const properties = {
    type:    { type: 'string', enum: allowedActionTypes, description: 'Which action this proposal performs. MUST be one of the allowed values for this response.' },
    summary: { type: 'string', description: 'One-line summary the athlete reads on the card. <= 100 chars ideal.' },
  };
  const required = ['type', 'summary'];
  for (const t of allowedActionTypes) {
    properties[t] = { anyOf: [PAYLOAD_BY_TYPE[t], { type: 'null' }], description: `Populated only when type=${t}.` };
    required.push(t);
  }
  return {
    type: 'object',
    description,
    properties,
    required,
    additionalProperties: false,
  };
}

function buildEnvelopeSchema(allowedActionTypes, { description, name } = {}) {
  const envelope = {
    type: 'object',
    description: description || 'Structured coach response. summary_message is shown as the bot message text; proposals[] renders as Confirm/Discard cards below.',
    properties: {
      summary_message: {
        type: 'string',
        description: 'Concise coaching message (≤4 short sentences). The proposals carry the detail; this prose explains the WHY and the strategic framing only. Never duplicate envelope content here.',
      },
      proposals: {
        type: 'array',
        description: 'Zero or more changes to propose. Empty array = pure advice / clarifying question. Each entry renders as a Confirm/Discard card.',
        items: buildProposalSchema(
          allowedActionTypes,
          `One action proposed to the athlete. Populate the per-type payload field matching this proposal's "type"; set the others to null.`
        ),
      },
    },
    required: ['summary_message', 'proposals'],
    additionalProperties: false,
  };
  return {
    envelope,
    responseFormat: {
      type: 'json_schema',
      json_schema: {
        name: name || 'coach_actions_envelope',
        strict: true,
        schema: envelope,
      },
    },
  };
}

// ── Full envelope (all 7 action types) ──────────────────────────────────

const _full = buildEnvelopeSchema(ACTION_TYPES, {
  name: 'coach_actions_envelope',
  description: 'The full coaching response. summary_message is shown to the athlete as the bot message text; proposals[] drives the ProposalCard stack below.',
});
export const coachActionsEnvelope = _full.envelope;
export const responseFormat = _full.responseFormat;

// ── Phase-skeleton envelope (no workouts) ───────────────────────────────
// Used by the dedicated plan-build path. The narrow type enum makes
// add_workouts impossible to emit — the AI is forced to produce phase
// structure when asked to build a plan, instead of shortcutting into
// "dump everything as add_workouts" which has been the recurring failure
// mode for plan-build requests.

export const PHASE_SKELETON_ACTION_TYPES = [
  'add_phase',
  'modify_phase',
  'remove_phase',
  'add_milestone',
  'remove_milestone',
];

const _skeleton = buildEnvelopeSchema(PHASE_SKELETON_ACTION_TYPES, {
  name: 'coach_actions_phase_skeleton',
  description: 'Phase-skeleton response. Used when the athlete asks to BUILD / CREATE / DESIGN / SET UP / POPULATE / UPDATE a training plan structure. Emit add_phase and add_milestone proposals to lay out the macro shape — DO NOT emit individual workouts here; the athlete will request those in a follow-up turn once the phases are confirmed.',
});
export const phaseSkeletonEnvelope = _skeleton.envelope;
export const phaseSkeletonResponseFormat = _skeleton.responseFormat;
